import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtMXN, fmtDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import Topbar from "../components/Topbar";
import PizzaSpinner from "../components/PizzaSpinner";
import EmptyState from "../components/EmptyState";
import ExpenseCategoryBadge from "../components/ExpenseCategoryBadge";
import { ArrowLeft, Plus, RotateCw, Zap, Calendar, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

const FREQUENCIES = ["monthly", "bimonthly", "quarterly", "annual"];

function NewTemplateModal({ open, onClose, onSuccess, categories, branches }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "", branch_id: "", category: "", subcategory: "",
    vendor_name: "", vendor_rfc: "", vendor_bank_name: "", vendor_bank_account: "",
    default_subtotal: "", default_tax: "",
    frequency: "monthly", day_of_month: 5, auto_approve: false, notes: "",
  });
  const [busy, setBusy] = useState(false);
  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error(t("recurring.modal.nameRequiredError"));
    if (!form.category) return toast.error(t("recurring.modal.categoryRequiredError"));
    if (!form.vendor_name.trim()) return toast.error(t("recurring.modal.providerRequiredError"));
    if (parseFloat(form.default_subtotal || 0) <= 0) return toast.error(t("recurring.modal.amountRequiredError"));
    setBusy(true);
    try {
      await api.post("/recurring-expense-templates", {
        ...form,
        branch_id: form.branch_id || null,
        subcategory: form.subcategory || null,
        default_subtotal: parseFloat(form.default_subtotal),
        default_tax: parseFloat(form.default_tax || 0),
        day_of_month: parseInt(form.day_of_month) || 5,
      });
      toast.success(t("recurring.modal.templateCreated"));
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("recurring.modal.createError"));
    } finally { setBusy(false); }
  };

  if (!open) return null;
  const selectedCat = categories.find((c) => c.id === form.category);
  const subs = selectedCat?.subcategories || [];

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white">
            <div>
              <h2 className="font-display font-bold text-lg">{t("recurring.modal.title")}</h2>
              <p className="text-xs text-slate-500 mt-0.5">{t("recurring.modal.subtitle")}</p>
            </div>
            <button data-testid="tpl-modal-close" onClick={onClose} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">{t("recurring.modal.nameRequired")}</label>
              <input data-testid="tpl-name-input" value={form.name} onChange={(e) => update("name", e.target.value)}
                placeholder={t("recurring.modal.namePh")} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">{t("expenses.categoryRequired")}</label>
                <select data-testid="tpl-category-select" value={form.category}
                  onChange={(e) => { update("category", e.target.value); update("subcategory", ""); }}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                  <option value="">{t("recurring.modal.selectPh")}</option>
                  {categories.map((c) => (<option key={c.id} value={c.id}>{t(`expense.categories.${c.id}`, { defaultValue: c.label })}</option>))}
                </select>
              </div>
              {subs.length > 0 && (
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">{t("expenses.subcatLabel")}</label>
                  <select data-testid="tpl-subcategory-select" value={form.subcategory} onChange={(e) => update("subcategory", e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                    <option value="">{t("recurring.modal.subOptional")}</option>
                    {subs.map((s) => <option key={s.id} value={s.id}>{t(`expense.subcategories.${s.id}`, { defaultValue: s.label })}</option>)}
                  </select>
                </div>
              )}
            </div>

            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">{t("recurring.modal.appliesBranch")}</label>
              <select data-testid="tpl-branch-select" value={form.branch_id} onChange={(e) => update("branch_id", e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                <option value="">{t("recurring.modal.corporate")}</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">{t("expenses.providerRequired")}</label>
                <input data-testid="tpl-vendor-input" value={form.vendor_name} onChange={(e) => update("vendor_name", e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">{t("common.rfc")}</label>
                <input value={form.vendor_rfc} onChange={(e) => update("vendor_rfc", e.target.value.toUpperCase())}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">{t("recurring.modal.subtotalRequired")}</label>
                <input data-testid="tpl-subtotal-input" type="number" step="0.01" value={form.default_subtotal}
                  onChange={(e) => update("default_subtotal", e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono text-right" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">{t("recurring.modal.baseTax")}</label>
                <input type="number" step="0.01" value={form.default_tax} onChange={(e) => update("default_tax", e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono text-right" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">{t("recurring.modal.frequencyRequired")}</label>
                <select data-testid="tpl-freq-select" value={form.frequency} onChange={(e) => update("frequency", e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                  {FREQUENCIES.map((k) => <option key={k} value={k}>{t(`recurring.frequencies.${k}`)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">{t("recurring.modal.dayOfMonth")}</label>
                <input type="number" min="1" max="28" value={form.day_of_month}
                  onChange={(e) => update("day_of_month", e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer p-3 bg-amber-50 rounded-lg border border-amber-200">
              <input data-testid="tpl-auto-approve-toggle" type="checkbox" checked={form.auto_approve}
                onChange={(e) => update("auto_approve", e.target.checked)} className="mt-0.5" />
              <div className="text-xs">
                <div className="font-bold text-amber-900">{t("recurring.modal.autoApproveTitle")}</div>
                <div className="text-amber-800 mt-0.5">{t("recurring.modal.autoApproveBody")}</div>
              </div>
            </label>
          </div>

          <div className="p-5 border-t border-slate-200 flex justify-end gap-3 sticky bottom-0 bg-white">
            <button onClick={onClose} className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold">{t("common.cancel")}</button>
            <button data-testid="tpl-submit-btn" onClick={submit} disabled={busy}
              className="btn-primary-chizy px-5 py-2.5 rounded-xl flex items-center gap-2 disabled:opacity-50">
              {busy ? t("recurring.modal.saving") : t("recurring.modal.createBtn")}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function RecurringExpenses() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    const [tpls, cats, brs] = await Promise.all([
      api.get("/recurring-expense-templates"),
      api.get("/expense-categories"),
      api.get("/branches"),
    ]);
    setList(tpls.data);
    setCategories(cats.data);
    setBranches(brs.data);
  };
  useEffect(() => { load(); }, []);

  const generate = async (tplId, name) => {
    setBusyId(tplId);
    try {
      const { data } = await api.post(`/recurring-expense-templates/${tplId}/generate`);
      toast.success(t("recurring.generated", { folio: data.folio, name }));
      await load();
      setTimeout(() => navigate(`/gastos/${data.id}`), 500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("recurring.generateError"));
    } finally { setBusyId(null); }
  };

  const deactivate = async (tplId) => {
    if (!window.confirm(t("recurring.confirmDeactivate"))) return;
    setBusyId(tplId);
    try {
      await api.delete(`/recurring-expense-templates/${tplId}`);
      toast.success(t("recurring.deactivated"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setBusyId(null); }
  };

  if (!list) return <><Topbar title={t("recurring.title")} /><PizzaSpinner /></>;

  const activeList = list.filter((tpl) => tpl.active);
  const canCreate = user?.role === "corporate_admin";

  return (
    <>
      <Topbar title={t("recurring.title")} subtitle={t("recurring.subtitle")} />
      <div className="p-6 max-w-6xl mx-auto space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button onClick={() => navigate("/gastos")}
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#1A1A1A]">
            <ArrowLeft className="w-4 h-4" /> {t("recurring.backToExpenses")}
          </button>
          {canCreate && (
            <button data-testid="tpl-new-btn" onClick={() => setOpen(true)}
              className="btn-primary-chizy px-4 py-2.5 rounded-xl text-sm flex items-center gap-2">
              <Plus className="w-4 h-4" /> {t("recurring.newTemplate")}
            </button>
          )}
        </div>

        {activeList.length === 0 ? (
          <EmptyState icon={RotateCw} title={t("recurring.emptyTitle")}
            message={canCreate ? t("recurring.emptyMsgCreate") : t("recurring.emptyMsgView")} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {activeList.map((tpl, i) => (
              <motion.div key={tpl.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-slate-300 transition-all"
                data-testid={`tpl-card-${tpl.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <div className="font-bold text-[#1A1A1A] text-base">{tpl.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{tpl.vendor_name} · {tpl.branch_name}</div>
                  </div>
                  <ExpenseCategoryBadge category={tpl.category} subcategory={tpl.subcategory} />
                </div>

                <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                  <div className="bg-slate-50 rounded-lg p-2.5">
                    <div className="text-[9px] uppercase font-bold text-slate-400">{t("recurring.baseAmount")}</div>
                    <div className="font-mono font-bold mt-1">{fmtMXN(tpl.default_total)}</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2.5">
                    <div className="text-[9px] uppercase font-bold text-slate-400 flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {t("recurring.frequency")}
                    </div>
                    <div className="font-bold mt-1">{t(`recurring.frequencies.${tpl.frequency}`)}</div>
                  </div>
                  <div className={`rounded-lg p-2.5 ${tpl.auto_approve ? "bg-amber-50" : "bg-slate-50"}`}>
                    <div className="text-[9px] uppercase font-bold text-slate-400 flex items-center gap-1">
                      <Zap className="w-3 h-3" /> {t("recurring.approval")}
                    </div>
                    <div className={`font-bold mt-1 ${tpl.auto_approve ? "text-amber-800" : "text-slate-700"}`}>
                      {tpl.auto_approve ? t("recurring.automatic") : t("recurring.manual")}
                    </div>
                  </div>
                </div>

                {tpl.last_generated_at && (
                  <div className="text-[10px] text-slate-400 mb-3">
                    {t("recurring.lastGenerated", { date: fmtDate(tpl.last_generated_at) })}
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <button data-testid={`tpl-generate-btn-${tpl.id}`} onClick={() => generate(tpl.id, tpl.name)} disabled={busyId === tpl.id}
                    className="flex-1 btn-primary-chizy py-2 rounded-lg text-xs flex items-center justify-center gap-1.5 disabled:opacity-50">
                    <RotateCw className="w-3.5 h-3.5" />
                    {busyId === tpl.id ? t("recurring.generating") : t("recurring.generate")}
                  </button>
                  {canCreate && (
                    <button data-testid={`tpl-delete-btn-${tpl.id}`} onClick={() => deactivate(tpl.id)} disabled={busyId === tpl.id}
                      className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-500 hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition disabled:opacity-50"
                      title={t("recurring.deactivateTooltip")}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <NewTemplateModal open={open} onClose={() => setOpen(false)} onSuccess={load} categories={categories} branches={branches} />
      </div>
    </>
  );
}
